import { ArrowRightOutlined, CheckCircleFilled } from '@ant-design/icons';
import { Button, Divider, Flex, Progress, Typography } from 'antd';
import type { EvidenceProgress } from 'api/brokerCases/types';
import { getProgressPercent } from 'pages/EvidencePage/helpers';
import { FormattedMessage } from 'react-intl';

export interface INextStepsProps {
  progress: EvidenceProgress;
  showBankDetailsTask?: boolean;
  isBankDetailsComplete?: boolean;
  onGoToEvidenceV2?: () => void;
  onGoToBankDetails?: () => void;
}

export const NextSteps = ({
  progress,
  showBankDetailsTask = false,
  isBankDetailsComplete = false,
  onGoToEvidenceV2,
  onGoToBankDetails,
}: INextStepsProps) => {
  const { totalCount, completedCount } = progress;
  const percent = getProgressPercent(completedCount, totalCount);
  const isEvidenceComplete = totalCount === completedCount;
  const isAllComplete = isEvidenceComplete && isBankDetailsComplete;

  return (
    <div className='w-full rounded-2xl bg-white'>
      <div className='px-6 py-3'>
        <Typography.Text className='mb-0 text-lg font-bold leading-8'>
          <FormattedMessage id='applicationDetails.nextSteps.title' />
        </Typography.Text>
      </div>
      <Divider className='my-0' />
      <div className='p-6'>
        <Flex className='gap-5 border-2 border-dashed border-slate-200 p-6 rounded-2xl'>
          {!isEvidenceComplete ? (
            <>
              <div className='relative h-20 w-20 shrink-0'>
                <Progress type='circle' percent={percent} size={80} format={() => null} strokeColor='#05DF72' />
                <div className='absolute inset-0 flex items-center justify-center'>
                  <Typography.Text className='text-center text-base font-bold leading-6'>
                    {completedCount} of {totalCount}
                  </Typography.Text>
                </div>
              </div>
              <Flex vertical className='gap-1'>
                <Typography.Text className='text-lg font-bold leading-8'>
                  <FormattedMessage id='applicationDetails.nextSteps.evidence.title' />
                </Typography.Text>
                <Typography.Text className='text-sm font-normal leading-5 text-gray-500'>
                  <FormattedMessage id='applicationDetails.nextSteps.evidence.description' />
                </Typography.Text>
                <Button
                  type='default'
                  className='mt-3 h-8 w-32 rounded-3xl border border-gray-200 bg-white px-4 text-sm font-normal leading-5 text-black shadow-none'
                  onClick={onGoToEvidenceV2}
                >
                  <FormattedMessage id='applicationDetails.nextSteps.evidence.goTo' />
                </Button>
              </Flex>
            </>
          ) : (
            <Flex align='center' className='gap-3'>
              <CheckCircleFilled className='text-[40px] leading-none text-green-500' />
              <Typography.Text className='text-base ml-1 font-semibold leading-8'>
                <FormattedMessage id='applicationDetails.nextSteps.evidence.completed' />
              </Typography.Text>
            </Flex>
          )}
        </Flex>

        {showBankDetailsTask && (
          <Flex
            align='center'
            justify='space-between'
            className='mt-4 gap-5 border-2 border-dashed border-slate-200 p-6 rounded-2xl'
          >
            {isBankDetailsComplete ? (
              <Flex align='center' className='gap-3'>
                <CheckCircleFilled className='text-[40px] leading-none text-green-500' />
                <Typography.Text className='text-base ml-1 font-semibold leading-8'>
                  <FormattedMessage id='applicationDetails.nextSteps.bankDetails.completed' />
                </Typography.Text>
              </Flex>
            ) : (
              <>
                <Flex vertical className='min-w-0 flex-1 gap-1'>
                  <Typography.Text className='text-lg font-bold leading-8'>
                    <FormattedMessage id='applicationDetails.nextSteps.bankDetails.title' />
                  </Typography.Text>
                  <Typography.Text className='text-sm font-normal leading-5 text-gray-500'>
                    <FormattedMessage id='applicationDetails.nextSteps.bankDetails.description' />
                  </Typography.Text>
                </Flex>
                <button
                  type='button'
                  aria-label='Go to bank details'
                  className='inline-flex h-10 w-10 shrink-0 flex-none appearance-none items-center justify-center rounded-full border border-solid border-gray-200 bg-white p-0 text-black shadow-none outline-none ring-0 hover:border-gray-300'
                  onClick={onGoToBankDetails}
                >
                  <ArrowRightOutlined className='text-sm leading-none' />
                </button>
              </>
            )}
          </Flex>
        )}

        {isAllComplete && (
          <div className='ant-flex ant-flex-align-center ant-flex-justify-space-between mt-4 text-center bg-[#F3F4F6] p-4 rounded-lg'>
            <Typography.Text className='ant-typography text-sm font-normal leading-5 text-gray-400 text-center'>
              <FormattedMessage id='applicationDetails.nextSteps.allComplete' />
            </Typography.Text>
          </div>
        )}
      </div>
    </div>
  );
};
